import { Link } from 'react-router-dom';
import { AlertTriangle, Activity } from 'lucide-react';
import { text, table, border, cx } from '../styles/theme';
import { TimeRibbon } from './TimeRibbon';
import { SourceBadge } from './SourceBadge';
import type { TimeBreakdown } from '../types/api';
import { formatAutonomy, formatDate, formatDuration, formatNumber } from '../utils/format';
import { TOTAL_TOKENS_LABEL, TOTAL_TOKENS_TOOLTIP, sumTotalTokens } from '../utils/tokenLabels';

interface SessionData {
  sessionId: string;
  aiTitle?: string; // AI-generated one-line headline; falls back to the short id
  project: string;
  hostname?: string;
  startedAt: string | number;
  durationMs: string | number;
  totalTurns: number;
  autonomyScore?: number;
  timeBreakdown?: TimeBreakdown;
  summaryGeneratedAt?: string | number;
  hashtags?: string[];
  anomalyFlags?: string[];
  narrativePreview?: string;
  isContinuation?: boolean; // lineage chip: this file continues another session
  source?: string; // log source ("claude_code" | "codex"); badges non-default sources
  executiveSummary?: string;
  inputTokens?: string;
  outputTokens?: string;
  cacheCreationTokens?: string;
  cacheReadTokens?: string;
}

interface SessionTableProps {
  sessions: SessionData[];
  showAutonomy?: boolean;
  showTimeBreakdown?: boolean;
  showHostname?: boolean;
  showTokens?: boolean;
  /**
   * Compact mode (dashboard "Recent Sessions"): hides the signals column and
   * uses a smaller cell font for a denser table. The full Sessions page leaves
   * this off to keep the attention-flag column.
   */
  compact?: boolean;
}

// Friction category tags read at a glance as "the operator had to step in":
// canonical correction/friction plus the legacy categories that map to them
// (frustration→correction, blocker/circular→friction).
const frictionTags = new Set(['correction', 'friction', 'frustration', 'blocker', 'circular']);

function frictionOf(session: SessionData): string[] {
  return (session.hashtags ?? []).filter((t) => frictionTags.has(t));
}

// A session is "flagged" if it needed correction/hit friction or tripped an
// anomaly — the only signals worth a dedicated column in a scannable list.
// Neutral category hashtags stay on Session Detail.
function isFlagged(session: SessionData): boolean {
  return frictionOf(session).length > 0 || (session.anomalyFlags?.length ?? 0) > 0;
}

// SignalCell collapses friction + anomaly into two compact count pills, with the
// underlying tag names in the tooltip, so the column stays narrow.
function SignalCell({ session }: { session: SessionData }) {
  const friction = frictionOf(session);
  const anomalies = session.anomalyFlags ?? [];
  if (friction.length === 0 && anomalies.length === 0) {
    return <span className={text.muted}>—</span>;
  }
  return (
    <div className="flex items-center justify-end gap-1.5">
      {friction.length > 0 && (
        <span
          role="img"
          aria-label={`Friction: ${friction.join(', ')}`}
          title={`Friction: ${friction.join(', ')}`}
          className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
        >
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          {friction.length}
        </span>
      )}
      {anomalies.length > 0 && (
        <span
          role="img"
          aria-label={`Anomalies: ${anomalies.join(', ')}`}
          title={`Anomalies: ${anomalies.join(', ')}`}
          className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
        >
          <Activity className="h-3 w-3" aria-hidden="true" />
          {anomalies.length}
        </span>
      )}
    </div>
  );
}

export function SessionTable({ sessions, showAutonomy = false, showTimeBreakdown = false, showHostname = false, showTokens = false, compact = false }: SessionTableProps) {
  const showSignals = !compact && sessions.some(isFlagged);
  const cellText = compact ? 'text-xs' : 'text-sm';

  const th = cx('px-4 py-3 text-left text-xs font-medium uppercase', table.headerText);
  const thRight = cx('px-4 py-3 text-right text-xs font-medium uppercase', table.headerText);

  let colSpan = 5;
  if (showSignals) colSpan++;
  if (showTimeBreakdown) colSpan++;
  if (showAutonomy) colSpan++;
  if (showHostname) colSpan++;
  if (showTokens) colSpan++;

  return (
    <table className={cx('min-w-full', table.divide)}>
      <thead className={cx('border-b', border.default)}>
        <tr>
          <th className={th}>Session</th>
          <th className={th}>Project</th>
          {showHostname && <th className={th}>Hostname</th>}
          <th className={th}>Started</th>
          <th className={thRight}>Duration</th>
          {showTimeBreakdown && (
            <th className={cx('px-4 py-3 text-xs font-medium uppercase', table.headerText)} style={{ minWidth: '120px' }}>
              Time Breakdown
            </th>
          )}
          <th className={thRight}>Turns</th>
          {showTokens && (
            <th className={cx(thRight, 'cursor-help')} title={TOTAL_TOKENS_TOOLTIP}>
              {TOTAL_TOKENS_LABEL}
            </th>
          )}
          {showAutonomy && <th className={thRight}>Autonomy</th>}
          {showSignals && <th className={thRight}>Signals</th>}
        </tr>
      </thead>
      <tbody className={table.divideMuted}>
        {sessions.length === 0 ? (
          <tr>
            <td colSpan={colSpan} className={cx('px-4 py-8 text-center', text.secondary)}>
              No sessions found
            </td>
          </tr>
        ) : (
          sessions.map((session) => (
            <tr key={session.sessionId} className={table.rowHover}>
              {/* Session cell: a human-readable AI title as the row headline
                  (so the list is scannable by what each session was about),
                  with the short id demoted to a secondary mono line. Falls back
                  to the id alone when no title has been generated yet. */}
              <td className={cx('px-4 py-3 max-w-xs', cellText)}>
                <div className="flex items-center gap-1.5">
                  <Link
                    to={`/sessions/${session.sessionId}`}
                    title={session.aiTitle || undefined}
                    className={cx(
                      'min-w-0 truncate hover:underline',
                      session.aiTitle ? cx('font-medium', text.primary) : cx('font-mono', text.primary),
                    )}
                  >
                    {session.aiTitle || `${session.sessionId.slice(0, 8)}…`}
                  </Link>
                  {session.summaryGeneratedAt && Number(session.summaryGeneratedAt) > 0 && (
                    <span
                      role="img"
                      aria-label="Summary available"
                      title="Summary available"
                      className="inline-block w-2 h-2 rounded-full bg-emerald-500 shrink-0"
                    />
                  )}
                  {session.isContinuation && (
                    <span
                      role="img"
                      aria-label="Continues an earlier session"
                      title="Continues an earlier session — open for the chain"
                      className="inline-block shrink-0 rounded bg-indigo-100 px-1 font-sans text-[10px] font-medium leading-4 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                    >
                      ⛓
                    </span>
                  )}
                  <SourceBadge source={session.source} />
                </div>
                {session.aiTitle && (
                  <div className={cx('mt-0.5 font-mono text-xs', text.muted)}>
                    {session.sessionId.slice(0, 8)}…
                  </div>
                )}
              </td>
              <td className={cx('px-4 py-3', cellText, text.primary)}>
                {session.project ? (
                  <Link
                    to={`/sessions?project=${encodeURIComponent(session.project)}`}
                    className={text.link}
                  >
                    {session.project}
                  </Link>
                ) : (
                  'Unknown'
                )}
              </td>
              {showHostname && (
                <td className={cx('px-4 py-3', cellText, text.primary)}>
                  {session.hostname ? (
                    <Link
                      to={`/sessions?hostname=${encodeURIComponent(session.hostname)}`}
                      className={text.link}
                    >
                      {session.hostname}
                    </Link>
                  ) : (
                    '—'
                  )}
                </td>
              )}
              <td className={cx('px-4 py-3 whitespace-nowrap', cellText, text.secondary)}>
                {formatDate(session.startedAt)}
              </td>
              <td className={cx('px-4 py-3 text-right font-mono tabular-nums', cellText, text.primary)}>
                {formatDuration(session.durationMs)}
              </td>
              {showTimeBreakdown && (
                <td className="px-4 py-3">
                  {session.timeBreakdown ? (
                    <TimeRibbon breakdown={session.timeBreakdown} height="h-1.5" />
                  ) : (
                    // No breakdown yet (un-extracted session): a bare bar reads as
                    // a zeroed/loading ribbon, so label it as genuinely absent for
                    // both hover and assistive tech.
                    <div
                      role="img"
                      aria-label="No time breakdown available"
                      title="No time breakdown available"
                      className="h-1.5 w-full rounded bg-navy-100 dark:bg-navy-700"
                    />
                  )}
                </td>
              )}
              <td className={cx('px-4 py-3 text-right font-mono tabular-nums', cellText, text.primary)}>
                {session.totalTurns}
              </td>
              {showTokens && (
                <td className={cx('px-4 py-3 text-right font-mono tabular-nums', cellText, text.primary)}>
                  {sumTotalTokens(session) > 0 ? (
                    formatNumber(sumTotalTokens(session))
                  ) : (
                    // An un-extracted session carries no token counters yet (an
                    // extracted session always has input tokens), so a bare "0"
                    // reads as "zero tokens measured" rather than "not measured".
                    // Show an explicit absent state, matching the Time Breakdown
                    // and Hostname columns.
                    <span
                      role="img"
                      aria-label="No token data yet"
                      title="No token data yet"
                      className={text.muted}
                    >
                      —
                    </span>
                  )}
                </td>
              )}
              {showAutonomy && (
                <td className={cx('px-4 py-3 text-right font-mono tabular-nums', cellText, text.secondary)}>
                  {formatAutonomy(session.autonomyScore)}
                </td>
              )}
              {showSignals && (
                <td className="px-4 py-3">
                  <SignalCell session={session} />
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
